import { html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";

import { SignalElement, inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";
import type { IRpcService } from "ui-common/services/rpc/types";
import "ui-components/button/wow-button.ts";
import "ui-components/sticky-button-container/wow-sticky-button-container.ts";
import "ui-components/wizard/wow-wizard";

import "@phosphor-icons/webcomponents/PhCircleNotch";

import { styles } from "./sign-up.css";

import "../components/sign-up-create/wow-sign-up-create";
import "../components/theme-selection/theme-selection";
import "../components/creator-follow/creator-follow";
import { t } from "../translations/i18n";
import { router, navigateTo } from "../router";

@customElement("sign-up-page")
export class SignUpPage extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  static override styles = [styles];

  @state()
  private currentStep = 0;

  @state()
  private selectedThemes: string[] = [];

  @state()
  private followedCreators: string[] = [];

  @state() private isLoading = false;

  @state() private isProcessingCheckout = false;

  @state() private checkoutError = "";

  override connectedCallback() {
    super.connectedCallback();
    this.updateStateFromRouter();
    window.addEventListener("popstate", this.handlePopState);
  }

  override disconnectedCallback() {
    window.removeEventListener("popstate", this.handlePopState);
    super.disconnectedCallback();
  }

  private updateStateFromRouter() {
    const segment = router.remainingSegments[0];

    // Map URL segments to step numbers
    // /my/sign-up → step 0 (account creation)
    // /my/sign-up/themes → step 1 (theme selection)
    // /my/sign-up/creators → step 2 (creator follow)
    // /my/sign-up/welcome → step 3 (welcome)

    if (!segment) {
      this.currentStep = 0;
      return;
    }

    const stepMap: Record<string, number> = {
      themes: 1,
      creators: 2,
      welcome: 3,
    };

    const step = stepMap[segment];

    // Protected steps (1-3) require authentication
    if (step !== undefined && step >= 1 && step <= 3) {
      if (this.authState.isAuthenticated.get()) {
        this.currentStep = step;

        // Process pending checkout if on welcome step
        if (step === 3) {
          void this.processPendingCheckout();
        }
      } else {
        // Redirect unauthenticated users to step 0
        this.currentStep = 0;
        navigateTo("sign-up", true, []);
      }
    } else {
      // Unknown segment, default to step 0
      this.currentStep = 0;
    }
  }

  private handlePopState = () => {
    this.updateStateFromRouter();
    this.requestUpdate();
  };

  private async processPendingCheckout() {
    if (!this.authState.isAuthenticated.get() || this.currentStep !== 3) {
      return;
    }

    const checkoutData = sessionStorage.getItem("pendingCheckout");
    if (!checkoutData) {
      return;
    }

    this.isProcessingCheckout = true;
    this.checkoutError = "";

    try {
      const checkout = JSON.parse(checkoutData) as
        | { type: "one-time"; contentType: "post" | "link"; contentId: string; influencerId: string; price: number }
        | { type: "subscription"; influencerId: string };

      const returnUrl = sessionStorage.getItem("checkoutReturnUrl") || undefined;

      if (checkout.type === "one-time") {
        const response = await this.rpcService.call<{ url: string }>("create-one-time-checkout", {
          contentType: checkout.contentType,
          contentId: checkout.contentId,
          returnUrl,
        });

        sessionStorage.removeItem("pendingCheckout");
        sessionStorage.removeItem("checkoutReturnUrl");
        window.location.href = response.url;
      } else if (checkout.type === "subscription") {
        const response = await this.rpcService.call<{ url: string }>("create-subscription-checkout", {
          influencerId: checkout.influencerId,
          returnUrl,
        });

        sessionStorage.removeItem("pendingCheckout");
        sessionStorage.removeItem("checkoutReturnUrl");
        window.location.href = response.url;
      }
    } catch (error) {
      console.error("Error processing pending checkout:", error);
      this.checkoutError = error instanceof Error ? error.message : "Failed to process checkout";
      this.isProcessingCheckout = false;
    }
  }

  private handleStepChange(e: CustomEvent<{ step: number }>) {
    this.currentStep = e.detail.step;
  }

  private handleSignupSuccess() {
    // const wizard = this.renderRoot.querySelector("wow-wizard");
    // wizard?.next();
  }

  private handleThemeSelection(themes: string[]) {
    this.selectedThemes = themes;
  }

  private handleCreatorSelection(creators: string[]) {
    this.followedCreators = creators;
  }

  private async saveUserCategories() {
    if (this.selectedThemes.length === 0) return;

    try {
      this.isLoading = true;
      await this.rpcService.call("save-user-themes", {
        categoryIds: this.selectedThemes,
      });
    } catch (error) {
      console.error("Error saving user categories:", error);
      // Don't block the flow - let the user continue even if saving fails
    } finally {
      this.isLoading = false;
    }
  }

  private async followSelectedCreators() {
    if (this.followedCreators.length === 0) return;

    try {
      this.isLoading = true;
      await this.rpcService.call("follow-creators", {
        creators: this.followedCreators,
      });
    } catch (error) {
      console.error("Error following creators:", error);
      // Don't block the flow - let the user continue even if saving fails
    } finally {
      this.isLoading = false;
    }
  }

  private renderStep1Content() {
    if (this.currentStep !== 1) return nothing;

    return html`
      <div class="container">
        <div class="content">
          <div class="logo">
            <wow-logo></wow-logo>
          </div>
          <div class="scrollable-content">
            <h2 class="title">${t("sign-up.chooseThemeTitle")}</h2>
            <p class="description">${t("sign-up.chooseThemeDescription")}</p>
            <theme-selection
              @themes-selected=${(e: CustomEvent) => this.handleThemeSelection(e.detail.themes)}
            ></theme-selection>
          </div>
        </div>
        <wow-sticky-button-container>
          <wow-button
            full-width
            ?disabled=${this.selectedThemes.length === 0 || this.isLoading}
            @click=${async () => {
              // Save user categories before proceeding
              await this.saveUserCategories();
              navigateTo("sign-up", true, ["creators"]);
            }}
          >
            Continue ${this.isLoading ? html`<ph-circle-notch size="16px" class="spinner"></ph-circle-notch>` : nothing}
          </wow-button>
        </wow-sticky-button-container>
      </div>
    `;
  }

  private renderStep2Content() {
    if (this.currentStep !== 2) return nothing;

    return html`
      <div class="container">
        <div class="content">
          <div class="logo">
            <wow-logo></wow-logo>
          </div>
          <div class="scrollable-content">
            <h2 class="title">${t("sign-up.discoverCreatorsTitle")}</h2>
            <p class="description">${t("sign-up.discoverCreatorsDescription")}</p>
            <creator-follow
              .followedCreators=${this.followedCreators}
              .categoryIds=${this.selectedThemes}
              @creators-selected=${(e: CustomEvent) => this.handleCreatorSelection(e.detail.creators)}
            ></creator-follow>
          </div>
        </div>
        <wow-sticky-button-container>
          ${this.followedCreators.length === 0
            ? html`<wow-button
                full-width
                variant="neutral"
                @click=${() => {
                  navigateTo("sign-up", true, ["welcome"]);
                }}
              >
                Skip
              </wow-button>`
            : html`<wow-button
                full-width
                ?disabled=${this.isLoading}
                @click=${async () => {
                  await this.followSelectedCreators();
                  navigateTo("sign-up", true, ["welcome"]);
                }}
              >
                Continue
                ${this.isLoading ? html`<ph-circle-notch size="16px" class="spinner"></ph-circle-notch>` : nothing}
              </wow-button>`}
        </wow-sticky-button-container>
      </div>
    `;
  }

  private renderStep3Content() {
    if (this.currentStep !== 3) return nothing;

    this.style.setProperty("--wow-button-inner-display", "flex");

    if (this.isProcessingCheckout) {
      return html`
        <div class="container">
          <div class="content">
            <h2 class="title">Processing your purchase...</h2>
            <p>Please wait while we redirect you to checkout.</p>
            <ph-circle-notch size="32px" class="spinner"></ph-circle-notch>
          </div>
        </div>
      `;
    }

    if (this.checkoutError) {
      return html`
        <div class="container">
          <div class="content">
            <h2 class="title">Checkout Error</h2>
            <p style="color: var(--color-error, #dc2626);">${this.checkoutError}</p>
          </div>
          <wow-sticky-button-container>
            <wow-button
              @click=${() => {
                this.checkoutError = "";
                void this.processPendingCheckout();
              }}
              variant="primary"
            >
              Try Again
            </wow-button>
            <wow-button href="/feed" variant="neutral"> Continue to WOW </wow-button>
          </wow-sticky-button-container>
        </div>
      `;
    }

    return html`
      <div class="container">
        <div class="content">
          <h2 class="title">Welcome to WOW!</h2>
          <p>Your account is ready. What would you like to do next?</p>
        </div>
        <wow-sticky-button-container>
          <wow-button href="/feed" variant="primary"> Explore WOW </wow-button>
          <wow-button href="/my/onboard-creator?step=1" variant="neutral"> Become a Creator </wow-button>
        </wow-sticky-button-container>
      </div>
    `;
  }

  protected override render() {
    return html`
      <div>
        <wow-wizard .currentStep=${this.currentStep} .totalSteps=${4} @step-change=${this.handleStepChange}>
          <div class="scrollable-step" aria-label="step-0">
            <wow-sign-up-create @signup-success=${this.handleSignupSuccess}></wow-sign-up-create>
          </div>
          <div aria-label="step-1">${this.renderStep1Content()}</div>
          <div aria-label="step-2">${this.renderStep2Content()}</div>
          <div aria-label="step-3">${this.renderStep3Content()}</div>
        </wow-wizard>
      </div>
    `;
  }
}
